/**
 * Farming: patches, crops and how they grow.
 *
 * A patch is a map object anyone can walk up to, but what grows in it belongs to each character
 * (like RuneScape): you see your own crop, the person beside you sees theirs. A character's
 * patches live in `CharacterState.plots`, keyed by `plotKey(patch)`.
 *
 * Crops grow on the real clock, logged in or not, on a shared growth tick every five minutes (so
 * everyone's crops move on together, and a "farm run" can be timed). Nothing is stored per tick:
 * a plot holds when it was planted, and its stage is worked out whenever someone looks.
 *
 * The work at a patch: hoe out the weeds, plant, water each stage (a watered stage gives an extra
 * harvest), then pick the crop one at a time until the patch is spent. An empty patch grows weeds
 * again if it's left.
 */
import { BANDS, type Band, type MapObject } from '../map.js';
import type { SkillId } from './skills.js';
import type { ToolLine } from './tools.js';
import type { ItemId } from './items.js';
import { HERBS, HERB_IDS, type HerbId } from './herblore.js';

/** One growth tick, for everyone. */
export const GROW_MS = 5 * 60_000;
/** An empty patch grows back one level of weeds this often. */
export const WEED_MS = 10 * 60_000;
export const MAX_WEEDS = 3;

export type PatchKind = 'allotment' | 'herb' | 'tree' | 'fruit' | 'bush' | 'log';
export const PATCHES: Record<PatchKind, { name: string; desc: string }> = {
  allotment: { name: 'Allotment', desc: 'A long bed of turned earth, edged with planks. Vegetables and grain grow here.' },
  herb: { name: 'Herb patch', desc: 'A small raised bed for herbs.' },
  tree: { name: 'Tree patch', desc: 'A ring of stones round deep, dug earth. Plant a tree seed and, in time, fell your own tree.' },
  fruit: { name: 'Fruit tree patch', desc: 'A sheltered square of good soil for a fruit tree. Once grown it fruits again and again.' },
  bush: { name: 'Bush patch', desc: 'A round bed against a low wattle fence, for berry bushes. Once grown they fruit again and again.' },
  log: { name: 'Mushroom log', desc: 'A rotting log laid in the shade on damp moss. Push spawn into it and mushrooms come.' },
};
export const isPatchKind = (v: unknown): v is PatchKind => typeof v === 'string' && Object.prototype.hasOwnProperty.call(PATCHES, v);

/** How a crop is drawn in the patch. */
export type CropLook = 'potato' | 'onion' | 'cabbage' | 'wheat' | 'herb' | 'garlic' | 'carrot' | 'pumpkin' | 'bean' | 'tree' | 'fruit' | 'flax' | 'bush' | 'mushroom';

export interface CropDef {
  name: string;
  /** Plural for messages ("potatoes"). */
  plural: string;
  patch: PatchKind;
  level: number;
  /** The seed item, and how many it takes to sow the patch. */
  seed: ItemId;
  seeds: number;
  produce: ItemId;
  /** Growth stages before it's ready, and growth ticks per stage. */
  stages: number;
  ticksPerStage: number;
  plantXp: number;
  harvestXp: number;
  /** Picks before a patch is spent, before watering and luck. */
  lives: number;
  look: CropLook;
  /** Chance of disease at each stage, before compost, soil and protection. */
  disease: number;
  /** What a farmer takes to watch it for you (no disease, no pests). */
  protect: [ItemId, number];
  /** Leaf colour (herbs, trees), and fruit colour (fruit trees). */
  color?: string;
  fruit?: string;
  /** Only grows where the soil and weather suit it: a patch in this band or further out. */
  minBand?: Band;
  /** Trees: the skill the pickings train (felling for logs), and the tool and level it takes. */
  harvestSkill?: SkillId;
  harvestTool?: ToolLine;
  harvestLevel?: number;
  /** Trees: Farming xp for checking a grown tree, the first time. */
  checkXp?: number;
  /** Fruit trees: picked bare, the fruit grows back (one stage) and the tree stays. */
  regrows?: boolean;
  /** What else it's good for, for the guide. */
  use?: string;
}

const crop = (d: CropDef) => d;
const BASE_CROPS = {
  potato: crop({ name: 'Potatoes', plural: 'potatoes', patch: 'allotment', level: 1, seed: 'potato_seed', seeds: 3, produce: 'potato', stages: 4, ticksPerStage: 2, plantXp: 8, harvestXp: 9, lives: 3, look: 'potato', disease: 0.12, protect: ['compost', 2] }),
  onion: crop({ name: 'Onions', plural: 'onions', patch: 'allotment', level: 5, seed: 'onion_seed', seeds: 3, produce: 'wild_onion', stages: 4, ticksPerStage: 2, plantXp: 9.5, harvestXp: 10.5, lives: 3, look: 'onion', disease: 0.12, protect: ['potato', 6] }),
  cabbage: crop({ name: 'Cabbages', plural: 'cabbages', patch: 'allotment', level: 8, seed: 'cabbage_seed', seeds: 3, produce: 'cabbage', stages: 4, ticksPerStage: 2, plantXp: 10, harvestXp: 11.5, lives: 3, look: 'cabbage', disease: 0.12, protect: ['wild_onion', 6] }),
  // kitchen herbs and garlic, for the fancier dishes
  sage: crop({ name: 'Sage', plural: 'sage', patch: 'herb', level: 14, seed: 'sage_seed', seeds: 1, produce: 'sage', stages: 4, ticksPerStage: 3, plantXp: 14, harvestXp: 15, lives: 4, look: 'herb', disease: 0.12, protect: ['wild_onion', 4], color: '#9ab09a' }),
  garlic: crop({ name: 'Garlic', plural: 'garlic', patch: 'allotment', level: 20, seed: 'garlic_seed', seeds: 3, produce: 'garlic', stages: 4, ticksPerStage: 2, plantXp: 17, harvestXp: 18, lives: 3, look: 'garlic', disease: 0.12, protect: ['wheat', 4] }),
  rosemary: crop({ name: 'Rosemary', plural: 'rosemary', patch: 'herb', level: 30, seed: 'rosemary_seed', seeds: 1, produce: 'rosemary', stages: 4, ticksPerStage: 3, plantXp: 26, harvestXp: 28, lives: 4, look: 'herb', disease: 0.12, protect: ['garlic', 4], color: '#5a7a6a' }),
  saffron: crop({ name: 'Saffron', plural: 'saffron', patch: 'herb', level: 62, seed: 'saffron_corm', seeds: 1, produce: 'saffron', stages: 4, ticksPerStage: 4, plantXp: 50, harvestXp: 56, lives: 3, look: 'herb', disease: 0.14, protect: ['rosemary', 6], color: '#9a7ac8' }),
  // further out: crops that want the soil and weather of the middle band and beyond
  carrot: crop({ name: 'Carrots', plural: 'carrots', patch: 'allotment', level: 26, seed: 'carrot_seed', seeds: 3, produce: 'carrot', stages: 4, ticksPerStage: 2, plantXp: 21, harvestXp: 22, lives: 4, look: 'carrot', disease: 0.12, protect: ['garlic', 4], minBand: 'middle' }),
  pumpkin: crop({ name: 'Pumpkins', plural: 'pumpkins', patch: 'allotment', level: 48, seed: 'pumpkin_seed', seeds: 3, produce: 'pumpkin', stages: 5, ticksPerStage: 2, plantXp: 36, harvestXp: 40, lives: 4, look: 'pumpkin', disease: 0.13, protect: ['carrot', 6], minBand: 'outer' }),
  ridge_bean: crop({ name: 'Ridge beans', plural: 'ridge beans', patch: 'allotment', level: 70, seed: 'bean_seed', seeds: 3, produce: 'ridge_bean', stages: 5, ticksPerStage: 3, plantXp: 58, harvestXp: 64, lives: 5, look: 'bean', disease: 0.14, protect: ['pumpkin', 4], minBand: 'frontier' }),
  // trees: plant the seed, wait hours, check it over (a lot of Farming xp), then fell it yourself for logs
  oak_tree: crop({ name: 'Oak', plural: 'oak', patch: 'tree', level: 15, seed: 'acorn', seeds: 1, produce: 'oak_log', stages: 4, ticksPerStage: 6, plantXp: 14, harvestXp: 21, lives: 6, look: 'tree', disease: 0.08, protect: ['potato', 10], color: '#4e7a34', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 20, checkXp: 470 }),
  willow_tree: crop({ name: 'Willow', plural: 'willow', patch: 'tree', level: 30, seed: 'willow_cutting', seeds: 1, produce: 'willow_log', stages: 5, ticksPerStage: 6, plantXp: 25, harvestXp: 29, lives: 7, look: 'tree', disease: 0.08, protect: ['cabbage', 10], color: '#7a9a4a', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 26, checkXp: 1450 }),
  silverbark_tree: crop({ name: 'Silverbark', plural: 'silverbark', patch: 'tree', level: 45, seed: 'silverbark_seed', seeds: 1, produce: 'silverbark_log', stages: 6, ticksPerStage: 6, plantXp: 45, harvestXp: 76, lives: 7, look: 'tree', disease: 0.08, protect: ['carrot', 10], color: '#a8c0c8', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 38, checkXp: 3400, minBand: 'middle' }),
  yew_tree: crop({ name: 'Yew', plural: 'yew', patch: 'tree', level: 60, seed: 'yew_seed', seeds: 1, produce: 'yew_log', stages: 7, ticksPerStage: 8, plantXp: 80, harvestXp: 76, lives: 8, look: 'tree', disease: 0.08, protect: ['pumpkin', 10], color: '#2e5a2e', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 44, checkXp: 7100, minBand: 'outer' }),
  ancientyew_tree: crop({ name: 'Ancient yew', plural: 'ancient yew', patch: 'tree', level: 82, seed: 'ancientyew_seed', seeds: 1, produce: 'ancientyew_log', stages: 8, ticksPerStage: 10, plantXp: 150, harvestXp: 255, lives: 8, look: 'tree', disease: 0.08, protect: ['ridge_bean', 10], color: '#24442a', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 90, checkXp: 15500, minBand: 'frontier' }),
  // fruit trees: fruit again and again once they're grown
  apple_tree: crop({ name: 'Apple tree', plural: 'apples', patch: 'fruit', level: 27, seed: 'apple_seed', seeds: 1, produce: 'apples', stages: 6, ticksPerStage: 4, plantXp: 22, harvestXp: 9, lives: 6, look: 'fruit', disease: 0.09, protect: ['sage', 6], color: '#5a8a3a', fruit: '#c8402a', checkXp: 1200, regrows: true }),
  pear_tree: crop({ name: 'Pear tree', plural: 'pears', patch: 'fruit', level: 42, seed: 'pear_seed', seeds: 1, produce: 'pear', stages: 6, ticksPerStage: 5, plantXp: 34, harvestXp: 14, lives: 6, look: 'fruit', disease: 0.09, protect: ['rosemary', 4], color: '#4a7a3a', fruit: '#c8c050', checkXp: 2400, regrows: true, minBand: 'middle' }),
  cherry_tree: crop({ name: 'Cherry tree', plural: 'cherries', patch: 'fruit', level: 64, seed: 'cherry_seed', seeds: 1, produce: 'cherries', stages: 6, ticksPerStage: 6, plantXp: 60, harvestXp: 25, lives: 6, look: 'fruit', disease: 0.09, protect: ['pumpkin', 6], color: '#3a6a3a', fruit: '#a81a2a', checkXp: 5600, regrows: true, minBand: 'outer' }),
  // flax: spun into bowstrings at the wheel, so a fletcher never runs short of yarn
  flax: crop({ name: 'Flax', plural: 'flax', patch: 'allotment', level: 34, seed: 'flax_seed', seeds: 3, produce: 'flax', stages: 4, ticksPerStage: 2, plantXp: 27, harvestXp: 29, lives: 5, look: 'flax', disease: 0.12, protect: ['wheat', 8], use: 'Spin it into bowstrings at a spinning wheel.' }),
  // berry bushes: grow once, pick again and again (like fruit trees, but quicker and lower)
  redberry_bush: crop({ name: 'Redberry bush', plural: 'redberries', patch: 'bush', level: 10, seed: 'redberry_seed', seeds: 1, produce: 'redberries', stages: 5, ticksPerStage: 2, plantXp: 11, harvestXp: 4.5, lives: 4, look: 'bush', disease: 0.1, protect: ['potato', 4], color: '#4e7a3a', fruit: '#c8303a', checkXp: 64, regrows: true, use: 'Redberry pie, at a range.' }),
  cloudberry_bush: crop({ name: 'Cloudberry bush', plural: 'cloudberries', patch: 'bush', level: 36, seed: 'cloudberry_seed', seeds: 1, produce: 'cloudberries', stages: 5, ticksPerStage: 3, plantXp: 28, harvestXp: 12, lives: 5, look: 'bush', disease: 0.1, protect: ['flax', 4], color: '#5a8a4a', fruit: '#e8a040', checkXp: 520, regrows: true, minBand: 'middle', use: 'Cloudberry pie, at a range.' }),
  frostberry_bush: crop({ name: 'Frostberry bush', plural: 'frostberries', patch: 'bush', level: 56, seed: 'frostberry_seed', seeds: 1, produce: 'frostberries', stages: 6, ticksPerStage: 3, plantXp: 46, harvestXp: 20, lives: 5, look: 'bush', disease: 0.1, protect: ['pumpkin', 3], color: '#3e6a5a', fruit: '#8ab8e8', checkXp: 1500, regrows: true, minBand: 'outer', use: 'Frostberry pie, at a range.' }),
  emberberry_bush: crop({ name: 'Emberberry bush', plural: 'emberberries', patch: 'bush', level: 76, seed: 'emberberry_seed', seeds: 1, produce: 'emberberries', stages: 6, ticksPerStage: 4, plantXp: 70, harvestXp: 30, lives: 6, look: 'bush', disease: 0.1, protect: ['ridge_bean', 3], color: '#4a4a2a', fruit: '#f06020', checkXp: 3600, regrows: true, minBand: 'frontier', use: 'Emberberry pie, at a range: a hot one.' }),
  // mushroom logs: push spawn into a shady log and pick what comes up
  mushroom_log: crop({ name: 'Field mushrooms', plural: 'mushrooms', patch: 'log', level: 18, seed: 'mushroom_spawn', seeds: 1, produce: 'mushroom', stages: 4, ticksPerStage: 2, plantXp: 15, harvestXp: 8, lives: 6, look: 'mushroom', disease: 0.1, protect: ['cabbage', 4], color: '#d8c8a8', use: 'Stews, chowder, stamina tonic.' }),
  truffle_log: crop({ name: 'Truffles', plural: 'truffles', patch: 'log', level: 58, seed: 'truffle_spawn', seeds: 1, produce: 'truffle', stages: 5, ticksPerStage: 4, plantXp: 48, harvestXp: 44, lives: 4, look: 'mushroom', disease: 0.12, protect: ['pumpkin', 4], color: '#4a3a2a', minBand: 'outer', use: 'Boar pie and deep stew.' }),
  // the last trees: what the oldest woods are made of
  glasswood_tree: crop({ name: 'Glasswood', plural: 'glasswood', patch: 'tree', level: 86, seed: 'glasswood_seed', seeds: 1, produce: 'glasswood_log', stages: 8, ticksPerStage: 10, plantXp: 170, harvestXp: 280, lives: 8, look: 'tree', disease: 0.07, protect: ['ridge_bean', 12], color: '#9ad0d0', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 85, checkXp: 19000, minBand: 'frontier' }),
  worldroot_tree: crop({ name: 'Worldroot', plural: 'worldroot', patch: 'tree', level: 96, seed: 'worldroot_seed', seeds: 1, produce: 'worldroot_log', stages: 9, ticksPerStage: 10, plantXp: 210, harvestXp: 340, lives: 9, look: 'tree', disease: 0.06, protect: ['emberberries', 12], color: '#3a5a2a', harvestSkill: 'forestry', harvestTool: 'axe', harvestLevel: 95, checkXp: 26000, minBand: 'frontier' }),
  wheat: crop({ name: 'Wheat', plural: 'sheaves of wheat', patch: 'allotment', level: 12, seed: 'wheat_seed', seeds: 3, produce: 'wheat', stages: 4, ticksPerStage: 2, plantXp: 13, harvestXp: 14.5, lives: 4, look: 'wheat', disease: 0.13, protect: ['cabbage', 4] }),
} satisfies Record<string, CropDef>;
/** Herbs grow in herb patches: one seed, slower, a little more prone to disease. */
const HERB_CROPS = Object.fromEntries(HERB_IDS.map((id, i) => {
  const h = HERBS[id] as import('./herblore.js').HerbDef;
  return [id, crop({ name: h.name, plural: h.name.toLowerCase(), patch: 'herb', level: h.grow, seed: `${id}_seed` as ItemId, seeds: 1, produce: `grimy_${id}` as ItemId, stages: 4, ticksPerStage: 4, plantXp: h.plantXp, harvestXp: h.harvestXp, lives: 3, look: 'herb', disease: 0.14, protect: (i === 0 ? ['compost', 2] : [`grimy_${HERB_IDS[i - 1]}`, 4]) as [ItemId, number], color: h.color, ...(h.band ? { minBand: h.band } : {}) })];
})) as Record<HerbId, CropDef>;
/**
 * Higher crops pay much more per crop: xp needed per level climbs steeply and a crop takes as
 * long as it takes, so the xp has to climb with it (see docs/BALANCE_FARMING.md).
 */
export const FARM_XP_SCALE = { at: 37, power: 2.5 };
export const farmXpScale = (level: number) => 1 + Math.pow(level / FARM_XP_SCALE.at, FARM_XP_SCALE.power);
const scaled = (d: CropDef): CropDef => { const k = farmXpScale(d.level); return { ...d, plantXp: Math.round(d.plantXp * k * 10) / 10, harvestXp: d.harvestSkill ? d.harvestXp : Math.round(d.harvestXp * k * 10) / 10, ...(d.checkXp ? { checkXp: Math.round(d.checkXp * k) } : {}) }; };
export const CROPS = Object.fromEntries(Object.entries({ ...BASE_CROPS, ...HERB_CROPS }).map(([id, d]) => [id, scaled(d as CropDef)])) as Record<keyof typeof BASE_CROPS | HerbId, CropDef>;
export type CropId = keyof typeof BASE_CROPS | HerbId;
export const CROP_IDS = Object.keys(CROPS) as CropId[];
export const isCropId = (v: unknown): v is CropId => typeof v === 'string' && Object.prototype.hasOwnProperty.call(CROPS, v);
export const cropDef = (id: CropId): CropDef => CROPS[id];
/** The crop a seed grows. */
export const CROP_OF_SEED: Partial<Record<string, CropId>> = Object.fromEntries(CROP_IDS.map(id => [CROPS[id].seed, id]));

/**
 * One character's patch. Everything is optional: a patch the character has never touched is full
 * of weeds.
 */
export interface PlotState {
  /** Weeds when last worked (0..3), and when (ms). An empty patch gains a level every WEED_MS from then. */
  weeds?: number;
  weedsAt?: number;
  crop?: CropId;
  /** When it was planted (ms). */
  planted?: number;
  /** Stages that were watered while growing, and the last stage watered. */
  wet?: number;
  wetStage?: number;
  /** Picks left; set at the first pick. */
  left?: number;
  /** How good the soil is, 0..100 (a patch you've never worked starts at SOIL_START). It stays with the patch between crops. */
  soil?: number;
  /** The crop grown here last (planting something different is good for the soil). */
  last?: CropId;
  /** 1 compost, 2 rich compost, 3 blackloam: dug in before planting. */
  compost?: number;
  /** A scarecrow stands in it for this crop: no pests come. */
  scarecrow?: boolean;
  /** A farmer is watching it: no disease, no pests. */
  protected?: boolean;
  /** Stages a disease was cured at. */
  cures?: number[];
  /** Weedy stages that were weeded. */
  weeded?: number[];
  /** Picks lost to pests that weren't chased off. */
  nibbled?: number;
  /** Trees: checked over once grown (the Farming xp is paid then). */
  checked?: boolean;
}

export const SOIL_START = 20;
/** Compost, best last: how much of the disease chance it leaves, and picks it adds. */
export const COMPOSTS = [
  { item: null, name: 'none', disease: 1 },
  { item: 'compost' as ItemId, name: 'compost', disease: 0.65 },
  { item: 'rich_compost' as ItemId, name: 'rich compost', disease: 0.3 },
  { item: 'blackloam' as ItemId, name: 'blackloam', disease: 0.12 },
] as const;
export const soilOf = (p: PlotState | undefined) => p?.soil ?? SOIL_START;
export const soilWord = (s: number) => (s >= 80 ? 'rich' : s >= 55 ? 'good' : s >= 30 ? 'fair' : 'poor');
/** A number in 0..1 from a plot, a stage and a salt: the same every time it's asked. */
function roll(planted: number, stage: number, salt: number) {
  let h = (Math.floor(planted / 1000) ^ (stage * 0x9e3779b1) ^ (salt * 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0; h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0; h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}
/** Chance this crop falls sick at each stage. */
export function diseaseChance(p: PlotState): number {
  if (!p.crop || p.protected) return 0;
  return CROPS[p.crop].disease * COMPOSTS[Math.min(3, p.compost ?? 0)].disease * (1 - soilOf(p) / 250);
}
const weedChance = (p: PlotState) => 0.35 * (1 - soilOf(p) / 200);
/** Is stage k (1 .. stages-1) a weedy one? */
export const weedyStage = (p: PlotState, k: number) => !!p.planted && k >= 1 && roll(p.planted, k, 7) < weedChance(p);

/** The key a patch's plot is saved under. Town pieces keep their piece id through map rebuilds. */
/** Whether a crop can grow in a patch out in this band. */
export const growsIn = (d: CropDef, band: Band | undefined) => !d.minBand || BANDS[band ?? 'home'].order >= BANDS[d.minBand].order;
export const bandName = (b: Band) => ({ home: 'anywhere', middle: 'the middle band or further out', outer: 'the outer band or further out', frontier: 'the frontier', reserved: 'the Scorch' })[b];

export const plotKey = (o: Pick<MapObject, 'id' | 'props'>) => (o.props?.town ? `t:${o.props.town}` : `o:${o.id}`);

const growthTick = (ms: number) => Math.floor(ms / GROW_MS);

export interface PlotView {
  weeds: number;
  /** Diseased now (cure it before the next stage), or dead of it. */
  diseased: boolean;
  dead: boolean;
  /** Weeds have come up among the crop this stage. */
  weedyNow: boolean;
  soil: number;
  crop: CropId | null;
  /** 0 .. stages; equal to `stages` when it's ready to pick. */
  stage: number;
  stages: number;
  ready: boolean;
  /** Watered for the stage it's in now. */
  wetNow: boolean;
  /** Ms until the next stage (0 when ready or empty). */
  nextIn: number;
  /** Ms until it's ready to pick (0 when ready or empty). */
  readyIn: number;
}

/** What a plot looks like at `now`. */
export function plotView(p: PlotState | undefined, now: number): PlotView {
  if (!p || !p.crop || p.planted === undefined) {
    const w0 = p?.weeds ?? MAX_WEEDS, at = p?.weedsAt ?? 0;
    const weeds = p ? Math.min(MAX_WEEDS, w0 + Math.max(0, Math.floor((now - at) / WEED_MS))) : MAX_WEEDS;
    return { weeds, crop: null, stage: 0, stages: 0, ready: false, wetNow: false, nextIn: 0, readyIn: 0, diseased: false, dead: false, weedyNow: false, soil: soilOf(p) };
  }
  const d = CROPS[p.crop], passed = growthTick(now) - growthTick(p.planted);
  let stage = Math.min(d.stages, Math.floor(passed / d.ticksPerStage));
  // sickness: the first stage it fell sick at (and wasn't cured) holds it there; left a whole stage, it dies
  const sick = sickStage(p);
  if (sick !== null && stage >= sick) {
    const dead = passed >= (sick + 1) * d.ticksPerStage;
    return { weeds: 0, crop: p.crop, stage: sick, stages: d.stages, ready: false, wetNow: p.wetStage === sick, nextIn: 0, readyIn: 0, diseased: !dead, dead, weedyNow: false, soil: soilOf(p) };
  }
  const ready = stage >= d.stages;
  const tickEnd = (growthTick(now) + 1) * GROW_MS - now;
  const ticksToNext = ready ? 0 : d.ticksPerStage - (passed % d.ticksPerStage);
  const nextIn = ready ? 0 : tickEnd + (ticksToNext - 1) * GROW_MS;
  const readyIn = ready ? 0 : tickEnd + (d.stages * d.ticksPerStage - passed - 1) * GROW_MS;
  const weedyNow = !ready && weedyStage(p, stage) && !(p.weeded ?? []).includes(stage);
  return { weeds: 0, crop: p.crop, stage, stages: d.stages, ready, wetNow: p.wetStage === stage, nextIn, readyIn, diseased: false, dead: false, weedyNow, soil: soilOf(p) };
}

/** The first stage this crop falls sick at and hasn't been cured, or null. */
export function sickStage(p: PlotState): number | null {
  if (!p.crop || p.planted === undefined) return null;
  const d = CROPS[p.crop], chance = diseaseChance(p);
  for (let k = 1; k < d.stages; k++) if (!(p.cures ?? []).includes(k) && roll(p.planted, k, 3) < chance) return k;
  return null;
}

/** Picks a grown crop gives before luck: lives, plus watering, compost and soil, less weeds left in and pests. */
export function cropLives(p: PlotState): number {
  if (!p.crop) return 0;
  const d = CROPS[p.crop];
  let unweeded = 0;
  for (let k = 1; k < d.stages; k++) if (weedyStage(p, k) && !(p.weeded ?? []).includes(k)) unweeded++;
  return Math.max(1, d.lives + (p.wet ?? 0) + (p.compost ?? 0) + Math.floor(soilOf(p) / 25) - unweeded - (p.nibbled ?? 0));
}

/** "12 min", "1 h 5 min", "under a minute". */
export function fmtWait(ms: number) {
  const m = Math.ceil(ms / 60_000);
  if (ms < 60_000) return 'under a minute';
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
}

/** A line about a plot, for tooltips and Examine. */
export function plotLine(p: PlotState | undefined, now: number): string {
  const v = plotView(p, now), soil = `Soil: ${soilWord(v.soil)}.`;
  if (!v.crop) return `${v.weeds ? `${['', 'A few weeds', 'Weedy', 'Overgrown'][v.weeds]}: hoe it clear to plant.` : `Clear and ready to plant${p?.compost ? `, with ${COMPOSTS[Math.min(3, p.compost)].name} dug in` : ''}.`} ${soil}`;
  const d = CROPS[v.crop];
  if (v.dead) return `${d.name}: dead of blight. Dig it up. ${soil}`;
  if (v.diseased) return `${d.name}: diseased! Treat it with plant cure before it grows on, or it dies. ${soil}`;
  if (v.ready) return `${d.name}: ready to pick${p?.left !== undefined ? ` (${p.left} left)` : ''}. ${soil}`;
  return `${d.name}: growing, stage ${v.stage + 1} of ${d.stages}${v.wetNow ? ', watered' : ''}${v.weedyNow ? ', weeds coming up' : ''}${p?.protected ? ', watched by a farmer' : ''}. Ready in about ${fmtWait(v.readyIn)}. Disease ${Math.round(diseaseChance(p!) * 100)}% a stage. ${soil}`;
}

/** Pests that come for a growing crop while you're near it: chase them off before they take some. */
export const PESTS = { crow: { name: 'crow', verb: 'Shoo the crow' }, rabbit: { name: 'rabbit', verb: 'Chase off the rabbit' } } as const;
export type PestKind = keyof typeof PESTS;

/** Watering cans: how much water each holds is in the tool's `holds`. */
export const WATER_PER_USE = 1;

/**
 * Growing contracts: a grower (Marlow, or the growers further out) asks for a crop you can
 * already grow, hands you the seed for it, and pays in xp, coins and a few seeds you might not
 * have yet when you bring the news it's done. Only crops that grow where the grower is.
 */
export interface FarmContract { crop: CropId | null; n: number; need: number; done: number }
export const CONTRACT_LEVEL = 10;
/** Crops a grower in this band could ask for: ones this level can grow, not too far below it. */
export function contractCrops(L: number, band: Band | undefined): CropId[] {
  const can = CROP_IDS.filter(id => CROPS[id].level <= L && growsIn(CROPS[id], band) && (!CROPS[id].harvestLevel || CROPS[id].level >= 15));
  const near = can.filter(id => CROPS[id].level >= L - 22);
  return near.length >= 3 ? near : can.sort((a, b) => CROPS[b].level - CROPS[a].level).slice(0, 4);
}
/** What counts: picks for most crops, the check for a tree you'll fell. */
export const contractNeed = (id: CropId) => { const d = CROPS[id]; return d.checkXp && !d.regrows ? 1 : d.regrows ? 5 : Math.min(4, d.lives); };
export const contractVerb = (id: CropId) => { const d = CROPS[id]; return d.checkXp && !d.regrows ? `Grow ${/^[AEIOU]/.test(d.name) ? 'an' : 'a'} ${d.name.toLowerCase()}${d.patch === 'tree' ? ' tree' : ''} and check it over` : `Grow and pick ${contractNeed(id)} ${d.plural}`; };
/** The pay for one: xp, coins. */
export function contractPay(id: CropId) {
  const d = CROPS[id], need = contractNeed(id);
  return { xp: Math.round((d.plantXp * 2 + (d.harvestSkill ? 0 : d.harvestXp * need) + (d.checkXp ?? 0) * 0.3) * 1.2), coins: 25 + d.level * 6 };
}
/** Seeds the grower adds as thanks: crops up to a few levels past yours, the rarer the better. */
export function contractSeeds(L: number, band: Band | undefined, r: () => number): [ItemId, number][] {
  const pool = CROP_IDS.filter(id => CROPS[id].level <= L + 6 && growsIn(CROPS[id], band));
  const out: [ItemId, number][] = [], n = 2 + Math.floor(r() * 2);
  for (let i = 0; i < n && pool.length; i++) {
    // lean towards the higher ones
    const k = Math.floor(Math.pow(r(), 0.6) * pool.length), id = pool.sort((a, b) => CROPS[a].level - CROPS[b].level)[Math.min(pool.length - 1, k)];
    const d = CROPS[id], prev = out.find(([s]) => s === d.seed);
    if (prev) prev[1] += d.seeds; else out.push([d.seed, d.seeds]);
  }
  return out;
}
